import { zodResolver } from '@hookform/resolvers/zod';

import { useCallback } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ApiError } from '@/api/error';
import { toast } from '@/app/store/toast.store';
import {
	signupSchema,
	type SignupFormValues,
} from '@/features/auth/schemas/signup.schema';
import type { SignupConsent } from '@/features/auth/types/signup-terms';
import type { InvitationSuccess } from '@/mock/invitation.mock';
import { useInvitationValidation } from '@/shared/hooks/use-invitation-validation';
import { useSignup } from '@/shared/hooks/use-signup';
import { ErrorCard } from './ErrorCard';
import { SignupForm } from './SignupForm';
import { ShcLogo } from "@/components/brand/ShcLogo";

export const MedicalSignupPage = () => {
	const { t } = useTranslation(['auth']);
	const invitationState = useInvitationValidation();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') ?? '';

	return (
		<main className='auth-page'>
			<div className='auth-card auth-card--lg'>
				{invitationState.status === 'loading' && <InvitationLoading />}

				{invitationState.status === 'error' && (
					<InvitationErrorView reason={invitationState.error.reason} t={t} />
				)}

				{invitationState.status === 'ready' && (
					<InvitationReady
						t={t}
						invitation={invitationState.invitation as InvitationSuccess}
						token={token}
					/>
				)}
			</div>
		</main>
	);
};

/** Loading skeleton while token is being validated */
function InvitationLoading() {
	return (
		<div className='p-[var(--space-8)] text-center'>
			<div className='w-14 h-14 rounded-[var(--radius-lg)] bg-[var(--muted)] mx-auto mb-[var(--space-4)]' />
			<div className='h-6 w-[60%] bg-[var(--muted)] rounded-[var(--radius-sm)] mx-auto mb-[var(--space-2)]' />
			<div className='h-4 w-[80%] bg-[var(--muted)] rounded-[var(--radius-sm)] mx-auto' />
		</div>
	);
}

/** Error state — maps reason to specific error message per PRD §3.2 */
function InvitationErrorView({
	reason,
	t,
}: {
	reason: 'expired' | 'already-registered' | 'invalid' | 'server-error';
	t: ReturnType<typeof useTranslation<['auth']>>['t'];
}) {
	const navigate = useNavigate();

	const messages: Record<typeof reason, string> = {
		expired: t('auth:signup.errors.invitation.expiredTitle'),
		'already-registered': t('auth:signup.errors.invitation.registeredTitle'),
		invalid: t('auth:signup.errors.invitation.invalidTitle'),
		'server-error': t('auth:signup.errors.invitation.serverErrorTitle'),
	};

	const title = messages[reason];

	return (
		<ErrorCard
			reason={reason}
			title={title}
			t={t}
			navigate={navigate}
			onRetry={
				reason === 'server-error' ? () => window.location.reload() : undefined
			}
		/>
	);
}

/** Valid invitation — owns the form state and the completeSignup mutation
 *  so the SignupForm itself stays presentational. SMS-specific errors
 *  remain in the form (they're tied to the phone field's RHF state). */
function InvitationReady({
	t,
	invitation,
	token,
}: {
	t: ReturnType<typeof useTranslation<['auth']>>['t'];
	invitation: InvitationSuccess;
	token: string;
}) {
	const navigate = useNavigate();
	const signup = useSignup(token);

	const formMethods: UseFormReturn<SignupFormValues> =
		useForm<SignupFormValues>({
			resolver: zodResolver(signupSchema),
			mode: 'onTouched',
			defaultValues: {
				email: invitation.email,
				institution: invitation.institution,
				department: invitation.department,
				role: invitation.role,
				agreedTermIds: [],
			},
		});

	const handleSubmit = useCallback(
		(
			data: SignupFormValues,
			phoneVerificationToken: string,
			consents: SignupConsent[],
		) => {
			signup.mutate(
				{
					password: data.password,
					name: data.name,
					// Pass the raw input; completeSignup's normalizePhone strips non-digits to the Korean
					// ^010\d{8}$ form — the same verified-phone key the OTP step stored. Korean-only, no "+".
					phone: data.phone,
					phoneVerificationToken,
					consents,
				},
				{
					onSuccess: () => {
						toast.success(t('auth:signup.messages.success'));
						navigate('/home');
					},
					onError: (err) => {
						if (!(err instanceof ApiError)) {
							toast.error(
								t('auth:signup.errors.invitation.serverErrorMessage'),
							);
							return;
						}
						switch (err.code) {
							case 'SIGNUP_TOKEN_EXPIRED':
								toast.error(t('auth:signup.errors.invitation.expiredMessage'));
								break;
							case 'SIGNUP_ALREADY_REGISTERED':
								toast.error(
									t('auth:signup.errors.invitation.registeredMessage'),
								);
								break;
							case 'SIGNUP_PHONE_UNVERIFIED':
								toast.error(
									t('auth:signup.errors.invitation.mobileVerificationRequired'),
								);
								break;
							case 'REQUIRED_TERMS_NOT_AGREED':
								toast.error(t('auth:signup.terms.requiredNotAgreed'));
								break;
							default:
								toast.error(
									t('auth:signup.errors.invitation.serverErrorMessage'),
								);
						}
					},
				},
			);
		},
		[signup, navigate, t],
	);

	return (
		<>
			{/* Logo + Title */}
			<div className='auth-card__logo'>
				<div className='auth-card__logo-icon'>
					<ShcLogo />
				</div>
				<h1 className='auth-card__title'>{t('auth:signup.title')}</h1>
			</div>
			<p className='auth-card__subtitle'>{t('auth:signup.subtitle')}</p>

			{/* Form */}
			<SignupForm
				formMethods={formMethods}
				onSubmit={handleSubmit}
				token={token}
				isPending={signup.isPending}
			/>

			{/* Login Link */}
			<div className='auth-card__links'>
				<Link to='/login'>{t('auth:signup.actions.goToLogin')}</Link>
			</div>
		</>
	);
}

export default MedicalSignupPage;
